/**
 * Module Settings — réglages mode classique. Phase 1, incrément 5.
 *
 * Port de `vSettings` du proto (l. 1893+) avec les sections suivantes :
 *   - Foyers (liste, bascule, ajout, renommer, supprimer)
 *   - Membres (avatar+nom+role, ajout local, retrait)
 *   - Niveaux d'accès (3 pills explicatifs)
 *   - Préférences (toggle notifs + version)
 *   - Langue (sélecteur 7 langues)
 *   - Légal (CGU, Confidentialité, Règlement) + Export RGPD + Suppression
 *
 * Sections SKIPPED jusqu'à Phase 4 (auth) :
 *   - Mon compte (currentUser n'existe pas encore)
 *   - Modifier mot de passe (auth)
 *   - Déconnexion (auth)
 *   - Inviter par email (envoi du lien magique en Phase 4 ; pour l'instant
 *     l'ajout local crée un membre directement, comme le mode local-only
 *     du proto).
 *
 * Toutes les chaînes UI passent par `t(key)` — cf. CLAUDE.md §3.6.
 */
import { html, type TemplateResult } from 'lit-html';
import { Icon, Icons } from '../../ui/icons.js';
import { classMap } from 'lit-html/directives/class-map.js';
import styles from './View.module.css';
import { getLang, setLang, SUPPORTED_LANGS, t, type Lang, type TKey } from '../../i18n/index.js';
import {
  addHome,
  addPerson,
  affectationOf,
  type Affectation,
  getCurrentHomeId,
  getHomes,
  isLastParent,
  isServerHomeId,
  removeHome,
  removeMember,
  renameHome,
  switchHome,
} from '../../stores/homes.js';
import { ApiError } from '../../api/client.js';
import { createInvitation, type InvitationRole } from '../../api/invitations.js';
import { getSession } from '../../stores/session.js';
import { showToast } from '../../ui/Toast.js';
import { getPreferences, setNotifs } from '../../stores/preferences.js';
import type { ClassicHomeData, Home, Member, Role } from '@nestor/shared-types';
import { navigate } from '../../lib/router.js';
import { editorialHero } from '../../ui/Hero.js';
import { accountSectionTemplate, loginModalTemplate, registerModalTemplate } from './account.js';
import { legalModalTemplate, openLegalModal, type LegalDocId } from './legal.js';
import { deleteLocalAccount, exportAllData } from './gdpr.js';
import { confirmDialog, promptDialog } from '../../ui/Dialog.js';
import { modalTemplate } from '../../ui/Modal.js';
import { bumpUi, closeModal, getModal, openModal } from '../../stores/ui.js';

const APP_VERSION = 'Nestor 1.0';

// Feedback alpha (item 4) : la création foyer / membre passe par des
// modales dédiées — les sections n'affichent plus que les listes.
const MODAL_ADD_HOME = 'settings:add-home';
const MODAL_ADD_MEMBER = 'settings:add-member';

/** Popover (?) des niveaux d'accès, ouvert dans la modale membre. */
let roleHelpOpen = false;

// Self-references : chaque langue est nommée DANS sa propre langue. Ces
// chaînes ne se traduisent volontairement pas. Marqué `i18n-allow` pour
// que le script anti-leaks les ignore (cf. apps/web/src/i18n/README.md).
const LANG_NAMES: Readonly<Record<Lang, string>> = {
  fr: 'Français' /* i18n-allow */,
  en: 'English' /* i18n-allow */,
  es: 'Español' /* i18n-allow */,
  de: 'Deutsch' /* i18n-allow */,
  pt: 'Português' /* i18n-allow */,
  it: 'Italiano' /* i18n-allow */,
  pl: 'Polski' /* i18n-allow */,
};

// Mapping rôle → libellé i18n (UI). Identifiants internes EN stables
// (`parent` / `teen` / `child`, §2.2 — audit B2) ; seule la couche
// d'affichage traduit. Les données locales legacy (`ado` / `enfant`)
// sont migrées à la lecture dans `stores/homes.ts`.
const ROLE_LABEL_KEY: Readonly<Record<Role, TKey>> = {
  parent: 'role_parent',
  teen: 'role_teen',
  child: 'role_child',
};

const ROLE_DESC_KEY: Readonly<Record<Role, TKey>> = {
  parent: 'role_parent_desc',
  teen: 'role_teen_desc',
  child: 'role_child_desc',
};

const ROLE_PILL_CLASS: Readonly<Record<Role, string>> = {
  parent: styles.rolePillParent!,
  teen: styles.rolePillTeen!,
  child: styles.rolePillChild!,
};

const AFFECT_LABEL_KEY: Readonly<Record<Exclude<Affectation, 'none'>, TKey>> = {
  principal: 'affect_principal',
  coparental: 'affect_coparental',
  both: 'affect_both',
};

// Couleurs d'avatar par défaut, déterministes selon l'id du membre.
// Le proto laisse l'utilisateur les choisir ; on simplifie en hash → palette.
const AVATAR_PALETTE = ['#7A9E8E', '#5B6C8F', '#C49060', '#9B7CB6', '#5B8DB8', '#D27C63'];

function avatarColorFor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]!;
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!)
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

// ── Handlers ──────────────────────────────────────────────────────────

function onSwitchHome(homeId: string): (e: Event) => void {
  return (e) => {
    e.preventDefault();
    if (switchHome(homeId)) {
      const h = getHomes().find((x) => x.id === homeId);
      navigate(h?.mode === 'coparenting' ? '/cp/' : '/');
    }
  };
}

function onRenameHome(homeId: string, currentName: string): (e: Event) => void {
  return (e) => {
    e.preventDefault();
    void promptDialog({ title: t('home_rename'), defaultValue: currentName }).then((next) => {
      if (next !== null) renameHome(homeId, next);
    });
  };
}

function onRemoveHome(homeId: string, name: string): (e: Event) => void {
  return (e) => {
    e.preventDefault();
    void confirmDialog({
      title: t('home_remove'),
      message: name,
      confirmLabel: t('remove'),
      danger: true,
    }).then((ok) => {
      if (ok) removeHome(homeId);
    });
  };
}

function onAddHomeSubmit(e: Event): void {
  e.preventDefault();
  const form = e.target as HTMLFormElement;
  const name = (form.elements.namedItem('home-name') as HTMLInputElement).value.trim();
  const mode = (form.elements.namedItem('home-mode') as HTMLSelectElement).value as
    | 'classic'
    | 'coparenting';
  if (!name) return;
  const h = addHome(name, mode);
  closeModal();
  switchHome(h.id);
  navigate(mode === 'coparenting' ? '/cp/' : '/');
  form.reset();
}

function onAddMemberSubmit(homeId: string): (e: Event) => void {
  return (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const name = (form.elements.namedItem('member-name') as HTMLInputElement).value;
    const role = (form.elements.namedItem('member-role') as HTMLSelectElement).value as Role;
    // Item 10d : cases d'affectation « affect-home-<id> ». Le foyer
    // courant est coché par défaut dans le template.
    const checks = form.querySelectorAll<HTMLInputElement>('input[name^="affect-home-"]:checked');
    const classicHomeIds: string[] = [];
    const coparentingHomeIds: string[] = [];
    checks.forEach((c) => {
      if (c.dataset['mode'] === 'coparenting') coparentingHomeIds.push(c.value);
      else classicHomeIds.push(c.value);
    });
    // Sécurité : si rien de coché, on retombe sur le foyer courant.
    if (classicHomeIds.length === 0 && coparentingHomeIds.length === 0) {
      classicHomeIds.push(homeId);
    }
    if (addPerson(name, role, { classicHomeIds, coparentingHomeIds })) {
      form.reset();
      closeModal();
    }
  };
}

/**
 * Retrait d'un membre — audit A1 :
 *   1. le **dernier parent** est protégé (invariant store §2.2) : clic
 *      → toast bienveillant qui explique, jamais de suppression ;
 *   2. tout autre retrait passe par une **confirmation** explicite.
 */
function onRemoveMember(homeId: string, member: Member): (e: Event) => void {
  return (e) => {
    e.preventDefault();
    if (isLastParent(homeId, member.id)) {
      showToast({ message: t('member_last_parent_hint'), kind: 'info' });
      return;
    }
    void confirmDialog({
      title: t('member_remove_title'),
      message: member.name,
      confirmLabel: t('member_remove'),
      danger: true,
    }).then((ok) => {
      // Re-garde au moment de l'exécution (l'état a pu changer pendant
      // que le dialog était ouvert) — le store refuse de toute façon.
      if (ok && !removeMember(homeId, member.id)) {
        showToast({ message: t('member_last_parent_hint'), kind: 'info' });
      }
    });
  };
}

function onLangChange(e: Event): void {
  const target = e.target as HTMLSelectElement;
  setLang(target.value as Lang);
}

function onToggleNotifs(): void {
  setNotifs(!getPreferences().notifs);
}

// ── Sections ──────────────────────────────────────────────────────────

function homeRowTemplate(home: Home, isActive: boolean): TemplateResult {
  return html`
    <li
      class=${classMap({ [styles.cardRow!]: true, [styles.cardRowActive!]: isActive })}
      data-testid="home-row"
      data-home-id=${home.id}
    >
      <span
        class=${classMap({
          [styles.modeDot!]: true,
          [styles.modeDotClassic!]: home.mode === 'classic',
          [styles.modeDotCoparenting!]: home.mode === 'coparenting',
        })}
        aria-hidden="true"
      ></span>
      <span class=${styles.cardName}>${home.name}</span>
      ${isActive ? html`<span class=${styles.activeChip}>${t('active')}</span>` : null}
      <div class=${styles.cardActions}>
        ${!isActive
          ? html`<button
              type="button"
              class=${styles.buttonGhost}
              data-testid=${`home-switch-${home.id}`}
              @click=${onSwitchHome(home.id)}
            >
              ${t('switch_home')}
            </button>`
          : null}
        <button
          type="button"
          class=${styles.buttonGhost}
          data-testid=${`home-rename-${home.id}`}
          aria-label=${t('rename')}
          title=${t('home_rename')}
          @click=${onRenameHome(home.id, home.name)}
        >
          ${Icon(Icons.pencil, 15)}
        </button>
        <button
          type="button"
          class=${styles.buttonDanger}
          data-testid=${`home-remove-${home.id}`}
          aria-label=${t('remove')}
          title=${t('home_remove')}
          @click=${onRemoveHome(home.id, home.name)}
        >
          ${Icon(Icons.trash, 15)}
        </button>
      </div>
    </li>
  `;
}

function homesSection(homes: readonly Home[], currentId: string): TemplateResult {
  return html`
    <fieldset class=${styles.section} data-testid="settings-homes">
      <legend>${t('my_homes')}</legend>
      ${homes.length === 0
        ? html`<p class=${styles.rowMeta}>${t('add_home')}</p>`
        : html`<ul class=${styles.list}>
            ${homes.map((h) => homeRowTemplate(h, h.id === currentId))}
          </ul>`}
      <button
        type="button"
        class=${styles.button}
        data-testid="open-add-home"
        @click=${(): void => {
          openModal(MODAL_ADD_HOME);
        }}
      >
        ${t('add_home')}
      </button>
    </fieldset>
  `;
}

/** Badge d'affectation d'un membre (item 10c). `none` → rien. */
function affectationBadge(member: Member): TemplateResult | null {
  const affect = affectationOf(member);
  if (affect === 'none') return null;
  return html`<span
    class=${classMap({ [styles.affectPill!]: true, [styles[`affect_${affect}`]!]: true })}
    data-testid="member-affect"
    >${t(AFFECT_LABEL_KEY[affect])}</span
  >`;
}

function memberRowTemplate(homeId: string, member: Member): TemplateResult {
  return html`
    <li class=${styles.cardRow} data-testid="member-row" data-member-id=${member.id}>
      <span class=${styles.avatar} style=${`background:${avatarColorFor(member.id)}`}>
        ${initialsOf(member.name)}
      </span>
      <div class=${styles.memberMain}>
        <span class=${styles.memberName}>${member.name}</span>
        <div class=${styles.memberMeta}>
          <span
            class=${classMap({ [styles.rolePill!]: true, [ROLE_PILL_CLASS[member.role]!]: true })}
          >
            ${t(ROLE_LABEL_KEY[member.role])}
          </span>
          ${affectationBadge(member)}
        </div>
      </div>
      <div class=${styles.cardActions}>
        <button
          type="button"
          class=${classMap({
            [styles.buttonDanger!]: true,
            [styles.buttonGuarded!]: isLastParent(homeId, member.id),
          })}
          data-testid=${`member-remove-${member.id}`}
          aria-label=${t('member_remove')}
          aria-disabled=${isLastParent(homeId, member.id) ? 'true' : 'false'}
          title=${isLastParent(homeId, member.id)
            ? t('member_last_parent_hint')
            : t('member_remove_title')}
          @click=${onRemoveMember(homeId, member)}
        >
          ${t('member_remove')}
        </button>
      </div>
    </li>
  `;
}

function membersSection(home: Home): TemplateResult {
  if (home.mode !== 'classic') {
    // En co-parentage, les membres sont gérés différemment (Phase 6).
    return html``;
  }
  const data = home.data as ClassicHomeData;
  return html`
    <fieldset class=${styles.section} data-testid="settings-members">
      <legend>${t('members')}</legend>
      ${data.members.length === 0
        ? html`<p class=${styles.rowMeta}>${t('invite')}</p>`
        : html`<ul class=${styles.list}>
            ${data.members.map((m) => memberRowTemplate(home.id, m))}
          </ul>`}
      <button
        type="button"
        class=${styles.button}
        data-testid="open-add-member"
        @click=${(): void => {
          roleHelpOpen = false;
          openModal(MODAL_ADD_MEMBER);
        }}
      >
        ${t('invite')}
      </button>
    </fieldset>
  `;
}

/**
 * Cases d'affectation aux foyers (item 10d). Affiche tous les foyers de
 * l'utilisateur ; le foyer courant est coché par défaut. `data-mode`
 * distingue classique / co-parentage pour l'aiguillage à la création.
 * Masqué s'il n'y a qu'un seul foyer (choix implicite).
 */
function affectationFieldset(currentHomeId: string): TemplateResult | null {
  const homes = getHomes();
  if (homes.length < 2) return null;
  return html`<fieldset class=${styles.affectFieldset} data-testid="add-member-affect">
    <legend class=${styles.affectLegend}>${t('member_affect_legend')}</legend>
    ${homes.map(
      (h) =>
        html`<label class=${styles.affectOption}>
          <input
            type="checkbox"
            name=${`affect-home-${h.id}`}
            value=${h.id}
            data-mode=${h.mode}
            ?checked=${h.id === currentHomeId}
          />
          <span
            >${h.name} ·
            ${t(h.mode === 'coparenting' ? 'affect_coparental' : 'affect_principal')}</span
          >
        </label>`,
    )}
  </fieldset>`;
}

/**
 * Section « Inviter un coparent » — Phase 6.2.
 *
 * Affichée si :
 *   - Le foyer courant a un `id` serveur (UUID — pas un foyer local
 *     pré-auth). Inviter sur un foyer qui n'existe pas encore côté
 *     serveur n'a pas de sens.
 *   - L'utilisateur est authentifié. Le check `role === parent` est
 *     enforce côté serveur (403 sinon — on l'attrape).
 *
 * Le state du formulaire vit dans une variable module-level (rerender
 * via `bumpUi`). Soumission → `createInvitation` (offline-first) →
 * toast i18n.
 */
const inviteFormState: {
  email: string;
  role: InvitationRole;
  submitting: boolean;
  error: string | null;
} = { email: '', role: 'parent', submitting: false, error: null };

function _setInviteForm(patch: Partial<typeof inviteFormState>): void {
  Object.assign(inviteFormState, patch);
  bumpUi();
}

async function _submitInvite(homeId: string): Promise<void> {
  if (inviteFormState.submitting) return;
  const email = inviteFormState.email.trim();
  if (!email) {
    _setInviteForm({ error: 'invite_email_label' });
    return;
  }
  _setInviteForm({ submitting: true, error: null });
  try {
    const result = await createInvitation(homeId, {
      email,
      role: inviteFormState.role,
    });
    // result.kind === 'sent' OU 'queued' — dans les deux cas, toast
    // success. Le toast 'queued' standard a déjà été affiché par
    // `notifyQueued` dans le replay engine.
    if (result.kind === 'sent') {
      showToast({ message: t('invite_sent_toast'), kind: 'success' });
    }
    _setInviteForm({ email: '', submitting: false });
  } catch (err) {
    let messageKey = 'invite_error_offline';
    if (err instanceof ApiError) {
      // 403 = pas parent. 400 = invalid email. 503 = R2/DB KO.
      messageKey = err.status === 403 ? 'invite_error_mismatch' : 'invite_error_offline';
    }
    showToast({ message: t(messageKey as TKey), kind: 'error' });
    _setInviteForm({ submitting: false });
  }
}

function invitationsSection(home: Home): TemplateResult | null {
  // Affichage conditionnel : foyer serveur + user authentifié.
  if (!isServerHomeId(home.id)) return null;
  const session = getSession();
  if (session.status !== 'authenticated') return null;

  return html`
    <fieldset class=${styles.section} data-testid="settings-invitations">
      <legend>${t('invite_section_title')}</legend>
      <div class=${styles.row}>
        <span class=${styles.rowMeta}>${t('invite_section_sub')}</span>
      </div>
      <div class=${styles.row}>
        <label>
          <span class=${styles.rowLabel}>${t('invite_email_label')}</span>
          <input
            type="email"
            data-testid="invite-email-input"
            placeholder=${t('invite_email_placeholder')}
            .value=${inviteFormState.email}
            @input=${(e: InputEvent): void => {
              _setInviteForm({ email: (e.target as HTMLInputElement).value });
            }}
          />
        </label>
      </div>
      <div class=${styles.row}>
        <label>
          <span class=${styles.rowLabel}>${t('invite_role_label')}</span>
          <select
            data-testid="invite-role-select"
            .value=${inviteFormState.role}
            @change=${(e: Event): void => {
              const v = (e.target as HTMLSelectElement).value as InvitationRole;
              _setInviteForm({ role: v });
            }}
          >
            <option value="parent">${t('invite_role_parent')}</option>
            <option value="teen">${t('invite_role_teen')}</option>
            <option value="child">${t('invite_role_child')}</option>
          </select>
        </label>
      </div>
      <div class=${styles.row}>
        <button
          type="button"
          class=${styles.button}
          data-testid="invite-submit-btn"
          ?disabled=${inviteFormState.submitting || !inviteFormState.email.trim()}
          @click=${(): void => {
            void _submitInvite(home.id);
          }}
        >
          ${t('invite_submit')}
        </button>
      </div>
    </fieldset>
  `;
}

/**
 * Popover (?) des niveaux d'accès — feedback alpha (item 4) : la
 * section dédiée disparaît, l'explication vit là où on en a besoin
 * (à la création d'un membre), derrière un petit rond « ? ».
 */
function roleHelpPopover(): TemplateResult | null {
  if (!roleHelpOpen) return null;
  const roles: Role[] = ['parent', 'teen', 'child'];
  return html`<div class=${styles.roleHelp} data-testid="role-help" role="note">
    ${roles.map(
      (r) =>
        html`<div class=${styles.roleHelpRow}>
          <span class=${classMap({ [styles.rolePill!]: true, [ROLE_PILL_CLASS[r]!]: true })}>
            ${t(ROLE_LABEL_KEY[r])}
          </span>
          <span>${t(ROLE_DESC_KEY[r])}</span>
        </div>`,
    )}
  </div>`;
}

/** Modale « Nouveau foyer » (feedback alpha, item 4). */
function addHomeModal(): TemplateResult | null {
  if (getModal() !== MODAL_ADD_HOME) return null;
  return modalTemplate({
    title: t('modal_add_home_title'),
    onClose: closeModal,
    testid: 'modal-add-home',
    content: html`<form @submit=${onAddHomeSubmit} data-testid="add-home-form">
      <div class=${styles.formInline}>
        <input
          type="text"
          name="home-name"
          data-testid="add-home-name"
          placeholder=${t('family_name')}
          required
          maxlength="100"
        />
        <select name="home-mode" data-testid="add-home-mode" aria-label=${t('home_type_label')}>
          <option value="classic">${t('home_type_classic')}</option>
          <option value="coparenting">${t('home_type_coparenting')}</option>
        </select>
        <button type="submit" class=${styles.button} data-testid="add-home-submit">
          + ${t('add')}
        </button>
      </div>
    </form>`,
  });
}

/** Modale « Nouveau membre » avec le (?) niveaux d'accès. */
function addMemberModal(homeId: string): TemplateResult | null {
  if (getModal() !== MODAL_ADD_MEMBER) return null;
  return modalTemplate({
    title: t('modal_add_member_title'),
    onClose: closeModal,
    testid: 'modal-add-member',
    content: html`<form @submit=${onAddMemberSubmit(homeId)} data-testid="add-member-form">
      <div class=${styles.formInline}>
        <input
          type="text"
          name="member-name"
          data-testid="add-member-name"
          placeholder=${t('invite')}
          required
          maxlength="100"
        />
        <label class=${styles.roleFieldLabel}>
          ${t('invite_role_label')}
          <button
            type="button"
            class=${styles.roleHelpBtn}
            data-testid="role-help-toggle"
            aria-label=${t('role_help_label')}
            aria-expanded=${roleHelpOpen ? 'true' : 'false'}
            @click=${(): void => {
              roleHelpOpen = !roleHelpOpen;
              bumpUi();
            }}
          >
            ?
          </button>
        </label>
        <select name="member-role" data-testid="add-member-role" aria-label=${t('access')}>
          <option value="parent">${t('role_parent')}</option>
          <option value="teen">${t('role_teen')}</option>
          <option value="child" selected>${t('role_child')}</option>
        </select>
        <button type="submit" class=${styles.button} data-testid="add-member-submit">
          + ${t('add')}
        </button>
      </div>
      ${roleHelpPopover()} ${affectationFieldset(homeId)}
    </form>`,
  });
}

function preferencesSection(): TemplateResult {
  const { notifs } = getPreferences();
  return html`
    <fieldset class=${styles.section} data-testid="settings-preferences">
      <legend>${t('preferences')}</legend>
      <div class=${styles.row}>
        <span class=${styles.rowLabel}>${t('notifs')}</span>
        <button
          type="button"
          class=${classMap({ [styles.toggle!]: true, [styles.toggleOn!]: notifs })}
          role="switch"
          aria-checked=${String(notifs)}
          aria-label=${t('notifs')}
          data-testid="notifs-toggle"
          @click=${onToggleNotifs}
        >
          <span class=${styles.toggleKnob}></span>
        </button>
      </div>
      <div class=${styles.row}>
        <span class=${styles.rowLabel}>${t('version')}</span>
        <span class=${styles.rowMeta} data-testid="app-version">${APP_VERSION}</span>
      </div>
    </fieldset>
  `;
}

function langSection(): TemplateResult {
  const current = getLang();
  return html`
    <fieldset class=${styles.section} data-testid="settings-lang">
      <legend>${t('lang_setting')}</legend>
      <label>
        ${t('lang_change')}
        <select
          data-testid="lang-select"
          aria-label=${t('lang_change')}
          .value=${current}
          @change=${onLangChange}
        >
          ${SUPPORTED_LANGS.map(
            (code) => html`
              <option value=${code} ?selected=${code === current}>${LANG_NAMES[code]}</option>
            `,
          )}
        </select>
      </label>
      <p class=${styles.rowMeta}>${t('lang_apply_info')}</p>
    </fieldset>
  `;
}

function openLegal(doc: LegalDocId): (e: Event) => void {
  return (e) => {
    e.preventDefault();
    openLegalModal(doc);
  };
}

function legalSection(): TemplateResult {
  return html`
    <div class=${styles.legal} data-testid="settings-legal">
      <button type="button" data-testid="legal-terms" @click=${openLegal('terms')}>
        ${t('terms')}
      </button>
      <span aria-hidden="true">·</span>
      <button type="button" data-testid="legal-privacy" @click=${openLegal('privacy')}>
        ${t('privacy')}
      </button>
      <span aria-hidden="true">·</span>
      <button type="button" data-testid="legal-rules" @click=${openLegal('rules')}>
        ${t('rules')}
      </button>
      <hr />
      <button
        type="button"
        data-testid="legal-export"
        @click=${(e: Event): void => {
          e.preventDefault();
          exportAllData();
          showToast({ message: t('gdpr_export_done'), kind: 'success' });
        }}
      >
        ${t('data_export')}
      </button>
      <span aria-hidden="true">·</span>
      <button
        type="button"
        class="danger"
        data-testid="legal-delete"
        @click=${(e: Event): void => {
          e.preventDefault();
          void deleteLocalAccount();
        }}
      >
        ${t('account_delete')}
      </button>
    </div>
  `;
}

// ── Vue principale ────────────────────────────────────────────────────

export function renderSettingsView(): TemplateResult {
  const homes = getHomes();
  const currentId = getCurrentHomeId();
  const current = homes.find((h) => h.id === currentId) ?? homes[0] ?? null;

  const isCp = current?.mode === 'coparenting';
  // Le rerender des formulaires login/register passe par `bumpUi()`
  // (le store ui notifie l'AppShell qui redraw — la modale est dans
  // l'AppShell). Pas de rerender local nécessaire.
  return html`
    <section data-testid="view-settings" class=${styles.view}>
      ${editorialHero({
        ...(current ? { badge: { mode: current.mode, label: current.name } } : {}),
        title: isCp ? t('settings_hero_title_cp') : t('settings_hero_title'),
        accent: isCp ? t('settings_hero_accent_cp') : t('settings_hero_accent'),
      })}
      ${accountSectionTemplate(bumpUi)} ${homesSection(homes, currentId)}
      ${current && current.mode === 'classic' ? membersSection(current) : null}
      ${current ? invitationsSection(current) : null} ${preferencesSection()} ${langSection()}
      ${legalSection()} ${loginModalTemplate(bumpUi)} ${registerModalTemplate(bumpUi)}
      ${legalModalTemplate()} ${addHomeModal()} ${current ? addMemberModal(current.id) : null}
    </section>
  `;
}
